import { CopyButton } from "@tracify/ui/copy-button";
import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from "nuqs";

import { toast } from "sonner";
import Table from "@components/ui/table";
import { TablePaginationPageJump } from "@components/ui/table-pagination";
import { Tooltip, TooltipTrigger, TooltipContent } from "@tracify/ui/tooltip";

import useCurrency from "@lib/hooks/use-currency";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import {
  formatRecentActivityApiDateParam,
  formatEventTypesParam,
  getDefaultRecentActivityRange,
  getRecentActivityRangeFromParams,
  parseEventTypesParam,
  type RecentActivityRange,
  validateRecentActivityRange,
} from "./recent-activity-utils";
import { RecentActivityToolbar } from "./RecentActivityToolbar";
import {
  RECENT_ACTIVITY_DEFAULT_ORDERING,
  useHeartbeat,
} from "./use-heartbeat";
import type { Activity, RecentActivityOrdering } from "./use-heartbeat";
import Spinner from "@tracify/ui/spinner";
import { exportRecentActivityCsv } from "./export-recent-activity";

dayjs.extend(relativeTime);
dayjs.extend(utc);
const columnHelper = createColumnHelper<Activity>();
const ACTIVITY_AMOUNT_BASE_CURRENCY = "EUR";
const RECENT_ACTIVITY_DEFAULT_PAGE_SIZE = 100;
const RECENT_ACTIVITY_ASC_ORDERING: RecentActivityOrdering = "timestamp";
const RECENT_ACTIVITY_DESC_ORDERING: RecentActivityOrdering = "-timestamp";

const getRecentActivityOrdering = (ordering: string): RecentActivityOrdering =>
  ordering === RECENT_ACTIVITY_ASC_ORDERING
    ? RECENT_ACTIVITY_ASC_ORDERING
    : RECENT_ACTIVITY_DEFAULT_ORDERING;

const RecentActivityTable = () => {
  const { convertToGlobalCurrency, globalCurrency } = useCurrency();
  const { selectedStoreIds } = useSelectedStores();
  const [isExporting, setIsExporting] = useState(false);
  const defaultRange = useMemo(() => getDefaultRecentActivityRange(), []);
  const [activityUrlState, setActivityUrlState] = useQueryStates({
    recentActivityEventTypes: parseAsString,
    recentActivityReturningCustomer: parseAsBoolean,
    recentActivityStart: parseAsString,
    recentActivityEnd: parseAsString,
    recentActivityOrdering: parseAsString.withDefault(
      RECENT_ACTIVITY_DEFAULT_ORDERING
    ),
    pageIndex: parseAsInteger.withDefault(0),
    pageSize: parseAsInteger.withDefault(RECENT_ACTIVITY_DEFAULT_PAGE_SIZE),
  });

  const isReturningCustomer =
    activityUrlState.recentActivityReturningCustomer ?? undefined;

  const pagination = useMemo<PaginationState>(
    () => ({
      pageIndex: activityUrlState.pageIndex,
      pageSize: activityUrlState.pageSize,
    }),
    [activityUrlState.pageIndex, activityUrlState.pageSize]
  );

  const ordering = getRecentActivityOrdering(
    activityUrlState.recentActivityOrdering
  );

  const sorting = useMemo<SortingState>(
    () => [
      {
        id: "time",
        desc: ordering === RECENT_ACTIVITY_DESC_ORDERING,
      },
    ],
    [ordering]
  );

  const selectedEventTypes = useMemo(
    () => parseEventTypesParam(activityUrlState.recentActivityEventTypes),
    [activityUrlState.recentActivityEventTypes]
  );

  const selectedRange = useMemo(
    () =>
      getRecentActivityRangeFromParams({
        startParam: activityUrlState.recentActivityStart,
        endParam: activityUrlState.recentActivityEnd,
        fallbackRange: defaultRange,
      }),
    [
      activityUrlState.recentActivityEnd,
      activityUrlState.recentActivityStart,
      defaultRange,
    ]
  );

  const rangeValidation = useMemo(
    () => validateRecentActivityRange(selectedRange),
    [selectedRange]
  );

  const { data, isLoading } = useHeartbeat({
    storeIds: selectedStoreIds,
    eventTypes: selectedEventTypes,
    isReturningCustomer,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    ordering,
    enabled: rangeValidation.isValid,
  });

  const events = data?.events ?? [];
  const hasActivity = (data?.totalEvents ?? events.length) > 0;

  const getActivityAmountDisplay = useCallback(
    ({
      amount,
      currency,
      localAmount,
    }: {
      amount: number | null | undefined;
      currency: string;
      localAmount?: number | null;
    }) => {
      if (amount === null || amount === undefined) {
        return {
          displayAmount: "-",
          originalAmount: null,
        };
      }

      const hasOriginalAmount =
        localAmount !== null && localAmount !== undefined;
      const shouldDisplayOriginalAmount =
        hasOriginalAmount && currency === globalCurrency;

      const displayAmount = shouldDisplayOriginalAmount
        ? convertToGlobalCurrency(localAmount, currency)
        : convertToGlobalCurrency(amount, ACTIVITY_AMOUNT_BASE_CURRENCY);

      return {
        displayAmount,
        originalAmount:
          hasOriginalAmount && !shouldDisplayOriginalAmount
            ? `${localAmount} ${currency}`
            : null,
      };
    },
    [convertToGlobalCurrency, globalCurrency]
  );

  const handleEventTypesChange = useCallback(
    (eventTypes: string[]) => {
      void setActivityUrlState(
        {
          recentActivityEventTypes: formatEventTypesParam(eventTypes),
          pageIndex: 0,
        },
        { history: "replace" }
      );
    },
    [setActivityUrlState]
  );

  const handleCustomerStatusChange = useCallback(
    (value: boolean | null) => {
      void setActivityUrlState(
        { recentActivityReturningCustomer: value, pageIndex: 0 },
        { history: "replace" }
      );
    },
    [setActivityUrlState]
  );

  const handleRangeChange = useCallback(
    (range: RecentActivityRange) => {
      void setActivityUrlState(
        {
          recentActivityStart: formatRecentActivityApiDateParam(
            range.startDate
          ),
          recentActivityEnd: formatRecentActivityApiDateParam(range.endDate),
          pageIndex: 0,
        },
        { history: "replace" }
      );
    },
    [setActivityUrlState]
  );

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updater) => {
      const nextPagination =
        typeof updater === "function" ? updater(pagination) : updater;

      void setActivityUrlState(
        {
          pageIndex: nextPagination.pageIndex,
          pageSize: nextPagination.pageSize,
        },
        { history: "replace" }
      );
    },
    [pagination, setActivityUrlState]
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const nextSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      const nextTimeSort = nextSorting.find((sort) => sort.id === "time");
      const nextOrdering =
        nextTimeSort?.desc === false
          ? RECENT_ACTIVITY_ASC_ORDERING
          : RECENT_ACTIVITY_DESC_ORDERING;

      void setActivityUrlState(
        {
          recentActivityOrdering: nextOrdering,
          pageIndex: 0,
        },
        { history: "replace" }
      );
    },
    [setActivityUrlState, sorting]
  );

  const handleDownloadCsv = useCallback(async () => {
    if (!rangeValidation.isValid) {
      toast.error(rangeValidation.message ?? "Select a valid date");
      return;
    }

    const selectedStoreId = selectedStoreIds[0];

    if (!selectedStoreId) {
      toast.error("Select a store to download recent activity");
      return;
    }

    if (!hasActivity) {
      toast.error("No recent activity to download");
      return;
    }

    const toastId = "recent-activity-export";

    toast.loading("Preparing recent activity export...", { id: toastId });

    try {
      setIsExporting(true);
      await exportRecentActivityCsv({
        storeId: selectedStoreId,
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
        eventTypes: selectedEventTypes,
        isReturningCustomer,
        onPollingStart: () => {
          toast.loading(
            "Generating export. Download will start automatically.",
            {
              id: toastId,
            }
          );
        },
      });
      toast.success("Downloaded recent activity export", { id: toastId });
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Failed to export recent activity. Please try again.",
        { id: toastId }
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    hasActivity,
    isReturningCustomer,
    rangeValidation.isValid,
    rangeValidation.message,
    selectedEventTypes,
    selectedRange,
    selectedStoreIds,
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("event", {
        enableSorting: false,
        enableResizing: false,
        header: "Event",
        size: 150,
        cell: (info) => (
          <p className="truncate font-bold ms-1">{info.getValue()}</p>
        ),
      }),
      columnHelper.accessor("isReturningCustomer", {
        enableSorting: false,
        enableResizing: false,
        header: "Customer Status",
        size: 170,
        cell: (info) => {
          if (info.row.original.event.toLowerCase() !== "purchase") {
            return <p>-</p>;
          }

          const status = info.getValue();
          return (
            <p>
              {status === true
                ? "Returning"
                : status === false
                  ? "New"
                  : "Unknown"}
            </p>
          );
        },
      }),

      columnHelper.accessor("host", {
        enableSorting: false,
        enableResizing: true,
        header: "Host",
        size: 250,
        cell: (info) => (
          <div className="flex items-center justify-between group cursor-pointer overflow-hidden">
            <p className="text-wrap wrap-break-word font-bold max-w-max line-clamp-2">
              {info.getValue() || "-"}
            </p>
          </div>
        ),
      }),

      columnHelper.accessor("screen", {
        enableSorting: false,
        enableResizing: true,
        header: "Path",
        size: 200,
        cell: (info) => (
          <div className="flex items-center justify-between group cursor-pointer overflow-hidden">
            <p className="text-wrap wrap-break-word font-bold max-w-max line-clamp-2">
              {info.getValue() || "-"}
            </p>
          </div>
        ),
      }),
      columnHelper.accessor("eventId", {
        enableSorting: false,
        enableResizing: false,
        header: "Event ID",
        size: 350,
        cell: (info) => (
          <div className="flex items-center justify-between gap-2 overflow-hidden">
            <p className="truncate font-bold">{info.getValue()}</p>
            <CopyButton value={info.getValue()} copyLabel="Event ID" />
          </div>
        ),
      }),
      columnHelper.accessor("time", {
        enableSorting: true,
        enableResizing: false,
        sortDescFirst: true,
        sortingFn: "datetime",
        header: "Time",
        size: 300,
        cell: (info) => {
          const originalDate = info.getValue();

          const utcDate = dayjs(originalDate);

          if (!utcDate.isValid()) {
            return <p>Invalid Date</p>;
          }

          return (
            <Tooltip>
              <TooltipTrigger>
                <p className="truncate font-bold">{dayjs(utcDate).fromNow()}</p>
              </TooltipTrigger>
              <TooltipContent>
                <div>
                  <div>
                    UTC:{" "}
                    {dayjs(utcDate).utc().format("MMM D, YYYY h:mm:ss A [UTC]")}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor(
        (row) => ({
          amount: row.amount,
          currency: row.currency,
          localAmount: row.localAmount,
        }),
        {
          id: "amount",
          enableSorting: false,
          enableResizing: false,
          header: "Amount",
          cell: (info) => {
            const { displayAmount, originalAmount } = getActivityAmountDisplay(
              info.getValue()
            );

            if (!originalAmount) {
              return <p className="truncate font-bold">{displayAmount}</p>;
            }

            return (
              <Tooltip>
                <TooltipTrigger>
                  <p className="truncate font-bold">{displayAmount}</p>
                </TooltipTrigger>
                <TooltipContent>{`Original: ${originalAmount}`}</TooltipContent>
              </Tooltip>
            );
          },
        }
      ),
    ],
    [getActivityAmountDisplay]
  );

  let loadingState;
  let emptyState;
  if (!rangeValidation.isValid) {
    emptyState = (
      <div className="h-16 flex w-[calc(100vw-320px)] justify-center items-center relative">
        <div className="flex flex-col items-center">
          <p className="font-semibold">Invalid Date</p>
          <p className="text-sm">{rangeValidation.message}</p>
        </div>
      </div>
    );
  } else if (isLoading) {
    loadingState = (
      <div className="relative flex w-[calc(100vw-320px)] justify-center items-center">
        <Spinner className="mr-2" size="large" />
        <p className="text-foreground">Loading your data...</p>
      </div>
    );
  } else if (!data || events.length === 0) {
    emptyState = (
      <div className="h-16 flex w-[calc(100vw-320px)] justify-center items-center relative">
        <div className="flex flex-col items-center">
          <p className="font-semibold">No Data Available</p>
          <p className="text-sm">
            There is no data available for the selected date or filters. Please
            try a different set of report settings
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <RecentActivityToolbar
        selectedEventTypes={selectedEventTypes}
        onEventTypesChange={handleEventTypesChange}
        isReturningCustomer={isReturningCustomer ?? null}
        onCustomerStatusChange={handleCustomerStatusChange}
        range={selectedRange}
        onRangeChange={handleRangeChange}
        rangeValidation={rangeValidation}
        isLoading={isLoading}
        isExporting={isExporting}
        hasRows={hasActivity}
        onDownloadCsv={handleDownloadCsv}
      />
      <Table<Activity>
        id="recent-activity-table"
        maxHeight="max-h-[calc(100vh-330px)] min-h-[280px]"
        columns={columns}
        data={events}
        loadingState={loadingState}
        emptyState={emptyState}
        enableResizing
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        paginationTotalRows={data?.totalEvents}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        disableRowsPerPage={data?.totalEvents === 0}
      >
        <TablePaginationPageJump />
      </Table>
    </div>
  );
};

export default RecentActivityTable;
